import { Box } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useAuth } from '@verantos-dev/core-auth';
import { ActionMenu, type ActionMenuItemProps } from '@verantos-dev/shared-components';

import { canEditConceptGroup, isConceptGroupOwner } from '../lib/conceptGroupPermissions';
import type { ProjectArtifact } from '../types';
import { DeleteConceptGroupDialog } from './DeleteConceptGroupDialog';
import { RenameConceptGroupDialog } from './RenameConceptGroupDialog';

interface ConceptGroupRowActionsProps {
  artifact: ProjectArtifact;
}

/**
 * Per-row action menu for concept-group rows on the artifacts list: Rename above
 * Delete. Both are hidden for Verantos-default groups and for anyone outside the
 * owning org (the gate the org-wide library's actions column uses,
 * `useConceptGroups.tsx`); Rename additionally requires a Draft the user owns. Owns
 * its own dialogs via local state (mirrors `AnalyticalDatasetRowActions`).
 */
export const ConceptGroupRowActions = ({ artifact }: ConceptGroupRowActionsProps) => {
  const intl = useIntl();
  const { projectId = '' } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);

  // TODO: cleanup with concept_group_rename ff
  const isRenameActionEnabled = Boolean(useFeatureFlagSplit('concept_group_rename'));

  const owner = (artifact.kindSpecific?.owner as string | null) ?? null;
  const organizationId = (artifact.kindSpecific?.organizationId as string | null) ?? null;

  const mayEdit = canEditConceptGroup({ owner, organizationId, user });
  const canDelete = mayEdit;
  // Renaming is Draft-only and owner-only, unlike Delete, which any member of the
  // owning org may do on either status (mirrors `AnalyticalDatasetRowActions`).
  const canRename =
    isRenameActionEnabled &&
    artifact.assetStatus === 'Draft' &&
    mayEdit &&
    isConceptGroupOwner(artifact.owner.id, user);

  if (!canRename && !canDelete) return null;

  const menu: ActionMenuItemProps[] = [
    ...(canRename
      ? [
          {
            id: `conceptGroupArtifactActionMenu-rename-${artifact.id}`,
            label: intl.formatMessage({ id: 'conceptGroups.actions.renameConceptGroup' }),
            onClick: () => setIsRenameOpen(true),
            icon: 'rename',
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            id: `conceptGroupArtifactActionMenu-delete-${artifact.id}`,
            label: intl.formatMessage({ id: 'conceptGroups.actions.deleteConceptGroup' }),
            onClick: () => setIsDeleteOpen(true),
            icon: 'delete',
          },
        ]
      : []),
  ];

  return (
    <Box data-testid={`concept-group-row-actions-${artifact.id}`} sx={{ display: 'inline-flex' }}>
      <ActionMenu
        id={`conceptGroupArtifactActionMenu-${artifact.id}`}
        menuItems={menu}
        // 264 matches AnalyticalDatasetRowActions; 227 crowded the longer labels.
        menuWidth={264}
        stopClickEventPropagation
      />
      {isRenameOpen && (
        <RenameConceptGroupDialog
          projectId={projectId}
          conceptGroupId={artifact.id}
          name={artifact.displayName ?? artifact.name}
          onClose={() => setIsRenameOpen(false)}
        />
      )}
      {isDeleteOpen && (
        <DeleteConceptGroupDialog
          projectId={projectId}
          conceptGroupId={artifact.id}
          name={artifact.name}
          onClose={() => setIsDeleteOpen(false)}
        />
      )}
    </Box>
  );
};
